import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../contexts/AuthContext';

const adminLoginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type AdminLoginFormData = z.infer<typeof adminLoginSchema>;

interface AdminLoginPageProps {
  onLoginSuccess: () => void;
}

export const AdminLoginPage: React.FC<AdminLoginPageProps> = ({ onLoginSuccess }) => {
  const { login } = useAuth();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminLoginFormData>({
    resolver: zodResolver(adminLoginSchema),
    defaultValues: {
      email: 'admin@school.edu',
      password: 'Admin123!',
    },
  });

  const onSubmit = async (data: AdminLoginFormData) => {
    setError('');
    setLoading(true);

    try {
      await login(data.email, data.password, 'admin');
      onLoginSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
        py: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 0 },
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            overflow: 'hidden',
            borderRadius: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              py: { xs: 4, sm: 5 },
              px: { xs: 3, sm: 4 },
              textAlign: 'center',
              color: 'white',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 64, sm: 80 },
                height: { xs: 64, sm: 80 },
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                mb: { xs: 2, sm: 3 },
                border: '2px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <AdminPanelSettings sx={{ fontSize: { xs: 36, sm: 48 }, color: 'white' }} />
            </Box>
            <Typography
              variant="h4"
              gutterBottom
              fontWeight={700}
              sx={{
                color: 'white',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Admin Portal
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              Access the administration dashboard
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 }, bgcolor: 'white' }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Test Accounts:
                </Typography>
                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                  admin@school.edu / Admin123! (Super Admin)
                </Typography>
                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                  sarah.johnson@school.edu / Sarah123!
                </Typography>
                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                  michael.chen@school.edu / Michael123!
                </Typography>
              </Alert>

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email Address"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Password"
                    type="password"
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    disabled={loading}
                    sx={{ mb: 3 }}
                  />
                )}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
                sx={{
                  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In to Admin'}
              </Button>
            </form>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
